/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React, {CSSProperties, ReactElement} from 'react';

// =========== INTERFACES

export interface MenuItem {
    label?: string;
    component?: React.ReactNode;
    subItems?: MenuItem[];
    onClick?: Function;
    normalCase?: boolean;
    id?: string;
    allowPropagation?: boolean;
}

export interface PasswordRule {
    label: string;
    validator: (password: string) => boolean;
}

export interface IndexedObject<T> {
    [key: string]: T;
}

export interface ColorSet {
    base: string;
    contrast: string;
}

export interface OrnamentShape {
    placement?: ElementPosition;
    element: React.ReactElement | string;
    allowPropagation?: boolean;
}

export interface ResponseData extends IndexedObject<any> {
    status: string;
    message?: string;
    success?: boolean;
}

export interface SelectItemShape {
    value: string | number,
    label: string | number | ReactElement,
    inputLabel?: string,
    selectable?: boolean,
    labelClass?: string;
    labelStyle?: CSSProperties;
    valueLabel?: string;

    [key: string]: any,
}

export interface DialogConfig {
    backgroundColor?: string;
    textColor?: string;
}

export interface PDFOptions {
    disableElements?: Array<'text' | 'image' | 'invoiceTable'>;
    maxWidth?: number | string;
    supportMultiPage?: boolean;
    enableTableHeaderColor?: boolean;
}

// =========== ENUMS

export enum ModeEnum {DETAIL = 'detail', EDIT = 'edit', NEW = 'new'}

export enum Breakpoint {XS = 0, SM = 600, MD = 960, LG = 1280, XL = 1920}

export enum MenuPlacement {TOP_LEFT, TOP_RIGHT, BOTTOM_RIGHT, BOTTOM_LEFT}

export enum ColorPickerType {
    SKETCH, PHOTOSHOP, CHROME, CIRCLE, TWITTER, BLOCK, COMPACT
}

export enum ColorPickerResultType {HEX, RGB, HSL}

export enum ControlVariant {OUTLINED = 'outlined', STANDARD = 'standard', SIMPLE = 'simple'}

export enum ControlPadding {SMALL = 'sp', NORMAL = 'np', LARGE = 'lp'}

export enum ElementPosition {LEFT = 'left', RIGHT = 'right', TOP = 'top', BOTTOM = 'bottom', CENTER = 'center'}

export enum EventType {CREATE, UPDATE, DELETE}

export enum FetchMethod {GET = 'GET', POST = 'POST', PUT = 'PUT', DELETE = 'DELETE', PATCH = 'PATCH'}

export enum PDFSettingType {TEXT, IMAGE}

export enum PDFElementType {TEXT = 'text', IMAGE = 'image', INVOICE_TABLE = 'invoice_table'}

export enum PDFElementSubType {INVOICE_TITLE = 'invoice_title'}

export enum PDFElementPersist {CONFIRM = 'confirm', PERSISTENT = 'persistent', NOT_PERSISTENT = 'not-persistent'}

export enum PdfFont {
    LATO = 'lato',
    MERRIWEATHER = 'merriweather',
    NOTO_SANS = 'noto-sans',
    NOTO_SERIF = 'noto-serif',
    OPEN_SANS = 'open-sans',
    PLAYFAIR = 'playfair',
    PT_SERIF = 'pt-serif',
    ROBOTO = 'roboto',
    ROBOTO_MONO = 'roboto-mono',
    HELVETICA = 'helvetica',
    SOURCE_SANS_PRO = 'source-sans-pro'
}

export enum MediaType {VIDEO, IMAGE}

export enum SideMenuMode {COMPACT, FULL}

export enum PDFDisplay {FIRST_PAGE = 'first_page', LAST_PAGE = 'last_page', ALL_PAGES = 'all_pages'}

// =========== TYPES
export type OptionsMenuItem = {
    label: string;
    icon?: React.ReactNode;
    iconPlacement?: ElementPosition;
    onClick?: Function;
    disabled?: boolean;
};

export type ErrorType = string | null;

export type CustomAppEvent = {
    eventName: string,
    handler: Function,
}

export type ChangeFunction<T = any> = (value: T, evt?: React.ChangeEvent) => void;
export type ChangeKeyFunction<K = string, V = any> = (key: K) => ChangeFunction<V>;
export type ModelResponse<T> = (obj: T, success: boolean | undefined, data: ResponseData) => void;
export type StrengthGauche = { validator: (password: string) => boolean };
export type PDFSetting = { key: string; label: string; type: PDFSettingType; value: any };

export type PDFElementParams<T extends PDFElementType, O extends IndexedObject<any>> = {
    key: string;
    type: T;
    sub_type?: PDFElementSubType | null;
    dimensions: { left: number; top: number; width: number; height: number };
    options: O;
    persistent?: PDFElementPersist;
    linkedID?: string;
}
export type DefaultPDFElementParams<T extends PDFElementType, O extends IndexedObject<any>> = Partial<{
    type: T;
    dimensions: Partial<{ left: number; top: number; width: number; height: number }>;
    options: O;
    persistent?: PDFElementPersist;
    key: string;
    linkedID?: string;
}>
export type PDFImageParams = { img: File | string | null, persistent: PDFElementPersist; alignment?: ElementPosition; display?: PDFDisplay };
export type PDFTextParams = { bold: boolean; underline: boolean; italic: boolean; fontSize: number; text: string; alignment?: ElementPosition; disabled?: boolean, font: PdfFont, color: string; persistent: PDFElementPersist; display?: PDFDisplay };
export type PDFInvoiceTableParams = {
    fontSize: number;
    widths: number[];
    font: PdfFont;
    color: string;
    headerColor: string;
    alignment?: ElementPosition;
    persistent: PDFElementPersist;
};
export type PDFInvoiceWidths = { name: number; price: number; quantity: number; subtotal: number }
export type PDFTextVariables = { company: IndexedObject<string>; client: IndexedObject<string>; invoice: IndexedObject<string>; }
export type PDFSubmitHandler = (items: PDFElementParams<any, any>[], callback?: VoidFunction) => void;
export type PDFElementDimensions = { left: number; top: number; width: number; height: number };

export type SidebarItem = {
    label: string; icon?: string | React.ReactNode;
    className?: string;
    id: string;
    onBottom?: boolean;
    route?: string | string[];
    exactRoute?: boolean;
    children?: SidebarItem[];
}
export type ResponseCallback = (data: ResponseData) => void;
export type RNDDimensions = { x: number, y: number, height: number, width: number };
export type HTMLVideoEvent = (evt: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
export type RenderFile = {
    filename: string;
    src: string;
}
export type Snack = {
    text: string | React.ReactElement;
    config?: DialogConfig;
}